import type { Practitioner, Reference } from '@medplum/fhirtypes'
import { type IDTRole, idtRoles } from '@seen/core/models/care-teams/constants'
import { ExtendedPractitioner } from '@seen/core/models/practitioner'

import { Button, cn } from '@seen/ui'
import { type JSX, useMemo } from 'react'
import { Combobox } from '#src/components/core/combobox/Combobox.tsx'
import { PractitionerNameplate } from '#src/components/nameplates/PractitionerNameplate.tsx'
import { UserPlaceholderNameplate } from '#src/components/nameplates/UserPlaceholderNameplate.tsx'
import { PractitionerOption } from '#src/components/options/PractitionerOption.tsx'
import { useSearch } from '#src/hooks/useSearch.ts'

// Combobox over practitioners holding the given IDT role.
export function RolePractitionerSelect({
  role,
  value,
  onChange,
  disabled,
  placeholder = 'Select practitioner…',
  className,
}: {
  role: IDTRole
  value?: Reference<Practitioner>
  onChange: (practitioner: Reference<Practitioner>) => void
  disabled?: boolean
  placeholder?: string
  className?: string
}): JSX.Element {
  // One shared query across every role's select; filtered client-side.
  const { data: practitioners } = useSearch(
    ExtendedPractitioner,
    ExtendedPractitioner.queryByIDTRoles(idtRoles)
  )

  const options = useMemo(
    () =>
      (practitioners ?? [])
        .filter((practitioner) => practitioner.roles?.includes(role))
        .map((practitioner) => ({
          value: practitioner.reference.reference,
          label: (
            <PractitionerOption
              practitioner={practitioner.reference}
              isSelected={practitioner.reference.reference === value?.reference}
            />
          ),
          keywords: [practitioner.name],
          entity: practitioner,
        })),
    [practitioners, role, value?.reference]
  )

  return (
    <Combobox<string>
      options={options}
      onSelect={(reference) => {
        const option = options.find((o) => o.value === reference)
        if (option) {
          onChange(option.entity.reference)
        }
      }}
      searchPlaceholder="Search practitioners…"
      disabled={disabled}
    >
      <Button
        variant="outline"
        disabled={disabled}
        className={cn(
          'flex items-center justify-start gap-2 shadow-sm',
          className
        )}
      >
        {value && <PractitionerNameplate practitioner={value} />}
        {!value && (
          <UserPlaceholderNameplate>{placeholder}</UserPlaceholderNameplate>
        )}
      </Button>
    </Combobox>
  )
}
